import { Episode, Project, EpisodeTranslationSummary } from '../../../types';
import { buildContentTabs, ContentTabDef } from './episodeTabs';

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface EpisodeContentCardProps {
  episode: Episode;
  projectData: Project | null;
  content: string;
  contentTab: string;
  originalContent: string;
  originalContentLoaded: boolean;
  normalizedContent: string;
  normalizedContentLoaded: boolean;
  translations: EpisodeTranslationSummary[];
  transContent: Record<string, string>;
  /** 尚无翻译记录、可手动发起翻译的预设语言（仅弹窗内使用，可选）。 */
  startableLanguages?: string[];
  onStartTranslation?: (language: string) => void;
  editingContent: boolean;
  editedContent: string;
  savingContent: boolean;
  normalizingEpisode: boolean;
  resolvingPronouns: boolean;
  t: Translate;
  onContentTabChange: (tab: string) => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onEditedContentChange: (content: string) => void;
  onSaveContent: () => void;
  onNormalize: () => void;
  onResolvePronouns: () => void;
  /** true 时卡片与内容区撑满父容器高度（用于弹窗内大尺寸展示）；默认 false=分集页原样。 */
  fillHeight?: boolean;
  /** 渲染在内容版本切换 tab 右侧的额外操作槽（如弹窗内的「去创作」按钮）；默认无。 */
  headerExtra?: React.ReactNode;
}

export default function EpisodeContentCard({
  episode,
  projectData,
  content,
  contentTab,
  originalContent,
  originalContentLoaded,
  normalizedContent,
  normalizedContentLoaded,
  translations,
  transContent,
  startableLanguages,
  onStartTranslation,
  editingContent,
  editedContent,
  savingContent,
  normalizingEpisode,
  resolvingPronouns,
  t,
  onContentTabChange,
  onStartEdit,
  onCancelEdit,
  onEditedContentChange,
  onSaveContent,
  onNormalize,
  onResolvePronouns,
  fillHeight = false,
  headerExtra,
}: EpisodeContentCardProps) {
  const tabs: ContentTabDef[] = buildContentTabs(episode, translations, t);
  const displayContent = contentTab === 'normalized'
    ? (normalizedContentLoaded ? normalizedContent : null)
    : contentTab === 'annotated'
      ? content
      : (originalContentLoaded ? originalContent : null);
  const isLoading = (contentTab === 'original' && !originalContentLoaded) || (contentTab === 'normalized' && !normalizedContentLoaded);

  // 当前激活的翻译 tab（若为内置 tab 则为 undefined，走原有渲染分支）。
  const activeTrans = tabs.find(x => x.key === contentTab && x.kind === 'translation');
  let translationBlock: React.ReactNode = null;
  if (activeTrans) {
    const lang = activeTrans.language as string;
    if (activeTrans.status === 'translating') translationBlock = <p style={{ color: 'var(--text-3)' }}>{t('content.translating')}</p>;
    else if (activeTrans.status === 'failed') translationBlock = <p style={{ color: 'var(--danger)' }}>{t('content.translateFailed')}</p>;
    else translationBlock = transContent[lang] !== undefined
      ? <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', fontSize: '0.9rem', color: 'var(--text-2)', margin: 0, overflowY: 'auto', flex: fillHeight ? 1 : undefined }}>{transContent[lang]}</pre>
      : <p style={{ color: 'var(--text-3)' }}>{t('loading')}</p>;
  }

  return (
    <div className="card theme-left-border" style={{ borderRadius: '10px', '--_theme-color': 'var(--theme-storyboard)', '--_theme-color-end': '#3b82f6', ...(fillHeight ? { height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 } : {}) } as React.CSSProperties}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {(tabs.length > 1 || (onStartTranslation && startableLanguages && startableLanguages.length > 0)) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {tabs.length > 1 && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0,
                  borderRadius: '10px', padding: '3px', border: '2px solid transparent',
                  background: 'linear-gradient(var(--surface), var(--surface)) padding-box, linear-gradient(135deg, var(--theme-script), #34d399) border-box',
                }}>
                  {tabs.map(tab => {
                    const active = contentTab === tab.key;
                    return (
                      <button
                        key={tab.key}
                        onClick={() => onContentTabChange(tab.key)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '5px',
                          border: 'none', cursor: 'pointer',
                          fontSize: '0.82rem', fontWeight: 600,
                          padding: '5px 16px', borderRadius: '8px', lineHeight: 1.4,
                          whiteSpace: 'nowrap', transition: '0.15s',
                          background: active ? 'linear-gradient(135deg, var(--theme-script), #34d399)' : 'transparent',
                          color: active ? '#fff' : 'var(--text-2)',
                          boxShadow: active ? '0 2px 8px color-mix(in srgb, var(--theme-script) 45%, transparent)' : 'none',
                        }}
                      >
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              )}
              {onStartTranslation && startableLanguages && startableLanguages.length > 0 && (
                <select
                  className="input"
                  style={{ fontSize: '0.78rem', padding: '2px 6px', width: 'auto' }}
                  value=""
                  onChange={(e) => { if (e.target.value) onStartTranslation(e.target.value); }}
                >
                  <option value="">{t('content.startTranslation')}</option>
                  {startableLanguages.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              )}
            </div>
          )}
          {headerExtra}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          {projectData?.enableScriptNormalization && episode.status !== 'normalizing' && (
            <button className="btn btn-secondary" onClick={onNormalize} disabled={normalizingEpisode}>
              {normalizingEpisode ? <><span className="loading-spinner"></span>{t('shot.submitting')}</> : episode.normalizedContentURL ? t('content.reNormalize') : t('content.normalize')}
            </button>
          )}
          {projectData && !projectData.enableScriptNormalization && episode.status !== 'resolving_pronouns' && (
            <button className="btn btn-secondary" onClick={onResolvePronouns} disabled={resolvingPronouns}>
              {resolvingPronouns ? <><span className="loading-spinner"></span>{t('shot.submitting')}</> : t('content.reResolvePronouns')}
            </button>
          )}
          {contentTab === 'original' && (
            !editingContent ? (
              <button className="btn btn-secondary" onClick={onStartEdit}>{t('edit', { ns: 'common' })}</button>
            ) : (
              <>
                <button className="btn btn-primary" onClick={onSaveContent} disabled={savingContent}>
                  {savingContent ? <><span className="loading-spinner"></span>{t('saving', { ns: 'common' })}</> : t('save', { ns: 'common' })}
                </button>
                <button className="btn btn-secondary" onClick={onCancelEdit}>{t('cancel', { ns: 'common' })}</button>
              </>
            )
          )}
        </div>
      </div>
      {activeTrans ? translationBlock : (
        contentTab === 'original' && editingContent ? (
          <textarea
            className="input textarea"
            style={{ minHeight: fillHeight ? 0 : '400px', flex: fillHeight ? 1 : undefined, fontFamily: 'monospace', fontSize: '0.9rem' }}
            value={editedContent}
            onChange={(event) => onEditedContentChange(event.target.value)}
          />
        ) : (
          displayContent ? (
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', fontSize: '0.9rem', color: 'var(--text-2)', margin: 0, maxHeight: fillHeight ? 'none' : '400px', minHeight: fillHeight ? 0 : undefined, flex: fillHeight ? 1 : undefined, overflowY: 'auto' }}>
              {displayContent}
            </pre>
          ) : isLoading ? (
            <p style={{ color: 'var(--text-3)' }}>{t('loading')}</p>
          ) : (
            <p style={{ color: 'var(--text-3)' }}>{t('content.noContent')}</p>
          )
        )
      )}
    </div>
  );
}
